import { Directive, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { IExtendedMouseEvent } from '@newroom-connect/library/interfaces';

@Directive({
  selector: '[nrcClickOutside]',
  standalone: true
})
export class ClickOutsideDirective {
  @Input() public targetContainerElement?: HTMLElement;
  // Array of CSS selectors to exclude
  @Input() public excludeQuerySelector?: string[];
  @Input() public isClickOustiseEnabled = true;

  @Output() public clickOutsideEvent = new EventEmitter<MouseEvent>();

  constructor(private readonly elementRef: ElementRef) {}

  /**
   * Listen for clicks and trigger a handler if the click occurs outside the host element and within the target container.
   *
   * @param event
   */
  @HostListener('document:click', ['$event'])
  public clickOutside(event: IExtendedMouseEvent): void {
    // Early return if directive is disabled
    if (!this.isClickOustiseEnabled) {
      return;
    }

    const target = event.target as Node;
    const host = this.elementRef.nativeElement;

    // Check if click is on excluded element
    if (this.isClickOnExcludedElement(target)) {
      return;
    }

    const isWithinTargetContainer = event.isSourceEmbedded || this.isWithinTargetContainer(target, event);

    if (this.isClickOutsideHost(target, host) && isWithinTargetContainer) {
      this.clickOutsideEvent.emit(event);
    }
  }

  /**
   * Determines if the click occurred on an excluded element.
   *
   * @param target The event target node.
   * @returns True if the click is on an excluded element.
   */
  private isClickOnExcludedElement(target: Node): boolean {
    if (!this.excludeQuerySelector || this.excludeQuerySelector.length === 0) {
      return false;
    }

    const element = target as Element;

    if (!element.matches || !element.closest) {
      return false;
    }

    return this.excludeQuerySelector.some(selector => {
      try {
        return element.matches(selector) || element.closest(selector);
      } catch {
        return false;
      }
    });
  }

  /**
   * Determines if the click occurred outside the host element.
   *
   * @param target The event target node.
   * @param host The host element.
   * @returns True if the click is outside the host element.
   */
  private isClickOutsideHost(target: Node, host: HTMLElement): boolean {
    return target !== host && !host.contains(target);
  }

  /**
   * Determines if the click is within the target container if set, or always true if not set.
   * If the event is dispatched from embedded content (e.g., via a custom property), allow for custom handling.
   *
   * @param target The event target node.
   * @param event The mouse event that triggered the click.
   * @returns True if the click is within the target container or if no container is set.
   */
  private isWithinTargetContainer(target: Node, event: IExtendedMouseEvent): boolean {
    // Check if the event was dispatched from embedded content
    const isSourceEmbedded = event.isSourceEmbedded === true;

    // If no container is set, always return true or if the event is from embedded content
    if (!this.targetContainerElement || isSourceEmbedded) {
      return true;
    }

    // Standard DOM containment check
    return this.targetContainerElement.contains(target);
  }
}
